import axios from 'axios';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const COINGECKO_API_URL = 'https://api.coingecko.com/api/v3';

export interface CryptoCurrency {
  id: string;             // CoinGecko ID (e.g., 'bitcoin')
  symbol: string;         // Symbol (e.g., 'btc')
  name: string;           // Full name (e.g., 'Bitcoin')
  image: string;          // URL to the image
  current_price: number;  // Current price in USD
  price_change_percentage_24h: number;  // 24h price change percentage
  market_cap: number;     // Market capitalization
  last_updated: string;   // Last updated timestamp
}

class CryptoPriceService {
  private defaultWatchlist = [
    'bitcoin',
    'ethereum',
    'ripple',
    'cardano',
    'solana',
    'polkadot',
    'dogecoin'
  ];
  
  // Get crypto price data for multiple currencies
  async getPrices(coinIds: string[]): Promise<CryptoCurrency[]> {
    try {
      if (coinIds.length === 0) {
        return [];
      }
      
      const response = await axios.get(`${COINGECKO_API_URL}/coins/markets`, {
        params: {
          vs_currency: 'usd',
          ids: coinIds.join(','),
          order: 'market_cap_desc',
          per_page: 100,
          page: 1,
          sparkline: false,
          price_change_percentage: '24h'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching crypto prices:', error);
      throw error;
    }
  }
  
  // Search for cryptocurrencies
  async searchCoins(query: string): Promise<any[]> {
    try {
      const response = await axios.get(`${COINGECKO_API_URL}/search`, {
        params: {
          query
        }
      });
      
      return response.data.coins.slice(0, 10); // Return top 10 results
    } catch (error) {
      console.error('Error searching for cryptocurrencies:', error);
      throw error;
    }
  }
  
  // Get the top cryptocurrencies by market cap
  async getTopCryptos(limit = 20): Promise<CryptoCurrency[]> {
    try {
      const response = await axios.get(`${COINGECKO_API_URL}/coins/markets`, {
        params: {
          vs_currency: 'usd',
          order: 'market_cap_desc',
          per_page: limit,
          page: 1,
          sparkline: false,
          price_change_percentage: '24h'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching top cryptocurrencies:', error);
      throw error;
    }
  }
  
  // Save user's watchlist to Firebase
  async saveWatchlist(userId: string, coinIds: string[]): Promise<void> {
    try {
      const userPrefsRef = doc(db, `users/${userId}/preferences/crypto_watchlist`);
      await setDoc(userPrefsRef, { coins: coinIds });
    } catch (error) {
      console.error('Error saving watchlist:', error);
      throw error;
    }
  }
  
  // Get user's watchlist from Firebase
  async getWatchlist(userId: string): Promise<string[]> {
    try {
      const userPrefsRef = doc(db, `users/${userId}/preferences/crypto_watchlist`);
      const docSnap = await getDoc(userPrefsRef);
      
      if (docSnap.exists() && docSnap.data().coins) {
        return docSnap.data().coins;
      }
      
      // If no watchlist exists, create one with defaults and return it
      await this.saveWatchlist(userId, this.defaultWatchlist);
      return this.defaultWatchlist;
    } catch (error) {
      console.error('Error getting watchlist:', error);
      return this.defaultWatchlist;
    }
  }
}

export const cryptoPriceService = new CryptoPriceService();
